import type { IsFullWidthRowParams, RowHeightParams } from "ag-grid-community";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { TableRef } from "@performyard/py-components";
import type { CompetencyDict } from "@performyard/service-contracts";

import type { SuperRowContext, SuperRowRow } from "./types";

const SUPER_ROW_SUFFIX = "__super";
export const superRowId = (parentRowId: string): string => `${parentRowId}${SUPER_ROW_SUFFIX}`;

const EMPTY_RANKS = new Set<number>();

// Height estimate for ag-grid before the content's own ResizeObserver corrects it to the real,
// text-dependent value — level/competency descriptions are free text, so exact line-wrapping isn't
// knowable up front. This only needs to be close enough to avoid a visible jump on open.
const BASE_HEIGHT = 96; // container padding + collapsed description block
const LEVEL_ROW_HEIGHT = 44; // one collapsed accordion row (name + caption + chevron)
const LEVEL_EXPANDED_EXTRA = 40; // extra for a level's opened description
const DESCRIPTION_EXPANDED_EXTRA = 56; // extra once the top description is expanded
// The levels card caps at this height and scrolls internally (mirrors SuperRowLayout's max-h class).
const LEVELS_MAX_HEIGHT = 400;

type UseSuperRowParams<TRow extends { competency_id: string }> = {
	rows: TRow[];
	competencies: CompetencyDict;
	getRowId: (row: TRow) => string;
	// Rank whose accordion row should already be open when the super row first appears — current level
	// for the assignment tables, expected level for the role interior; null/undefined pre-expands none.
	getInitialExpandedRank: (row: TRow) => number | null | undefined;
};

export function useSuperRow<TRow extends { competency_id: string }>({
	rows,
	competencies,
	getRowId,
	getInitialExpandedRank,
}: UseSuperRowParams<TRow>) {
	const gridRef = useRef<TableRef<SuperRowRow<TRow>>>(null);

	const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
	// Keyed by the PARENT row's _rowId — never the synthetic row's. Lives here, above the grid,
	// because applyTransaction destroys/recreates the full-width renderer instance on every toggle,
	// and getRowHeight (called by ag-grid, outside the renderer) needs to read it too.
	const [levelsExpanded, setLevelsExpanded] = useState<Record<string, Set<number>>>({});
	const [descriptionExpanded, setDescriptionExpanded] = useState<Record<string, boolean>>({});

	// Distinguishes "this row was just inserted" from "this row was already open" for the effect
	// below — only the latter needs an explicit redraw.
	const lastExpandedRowIdRef = useRef<string | null>(null);

	const contextRef = useRef<SuperRowContext>({
		expandedRowId: null,
		onExpandToggle: () => {},
		isLevelExpanded: () => false,
		onToggleLevel: () => {},
		isDescriptionExpanded: () => false,
		onToggleDescription: () => {},
	});

	const estimateHeight = (competencyId: string, expandedRanks: Set<number>, descriptionIsExpanded: boolean) => {
		const levelCount = competencies[competencyId]?.levels.length ?? 0;
		const levelsHeight = Math.min(
			levelCount * LEVEL_ROW_HEIGHT + expandedRanks.size * LEVEL_EXPANDED_EXTRA,
			LEVELS_MAX_HEIGHT
		);
		let height = BASE_HEIGHT + levelsHeight;
		if (descriptionIsExpanded) height += DESCRIPTION_EXPANDED_EXTRA;
		return height;
	};

	const removeExpandedRow = () => {
		const api = gridRef.current?.api;
		if (!api || !expandedRowId) return;
		const node = api.getRowNode(superRowId(expandedRowId));
		if (node?.data) api.applyTransaction({ remove: [node.data] });
	};

	const onExpandToggle = (rowId: string) => {
		const api = gridRef.current?.api;
		if (!api) return;

		const wasExpanded = expandedRowId === rowId;
		removeExpandedRow();

		if (wasExpanded) {
			setExpandedRowId(null);
			return;
		}

		const parentNode = api.getRowNode(rowId);
		if (!parentNode?.data) return;

		const parentRow = parentNode.data;
		const initialRank = getInitialExpandedRank(parentRow);
		setLevelsExpanded((prev) => ({
			...prev,
			[rowId]: initialRank != null ? new Set([initialRank]) : new Set(),
		}));
		setDescriptionExpanded((prev) => ({ ...prev, [rowId]: false }));

		const superRowData = {
			...parentRow,
			_rowId: superRowId(rowId),
			superRow: true,
			parentRowId: rowId,
		} as SuperRowRow<TRow>;

		const rowIndex = parentNode.rowIndex ?? 0;
		api.applyTransaction({ add: [superRowData], addIndex: rowIndex + 1 });
		setExpandedRowId(rowId);
	};

	const onToggleLevel = (rowId: string, rank: number) => {
		setLevelsExpanded((prev) => {
			const current = new Set(prev[rowId] ?? EMPTY_RANKS);
			if (current.has(rank)) current.delete(rank);
			else current.add(rank);
			return { ...prev, [rowId]: current };
		});
	};

	const onToggleDescription = (rowId: string) => {
		setDescriptionExpanded((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
	};

	const isLevelExpanded = (rowId: string, rank: number) => levelsExpanded[rowId]?.has(rank) ?? false;
	const isDescriptionExpanded = (rowId: string) => !!descriptionExpanded[rowId];

	// Keep the context object's identity stable but its contents current — ag-grid reads it fresh
	// on every applyTransaction/refreshCells call rather than through React's own render cycle.
	contextRef.current.expandedRowId = expandedRowId;
	contextRef.current.onExpandToggle = onExpandToggle;
	contextRef.current.isLevelExpanded = isLevelExpanded;
	contextRef.current.onToggleLevel = onToggleLevel;
	contextRef.current.isDescriptionExpanded = isDescriptionExpanded;
	contextRef.current.onToggleDescription = onToggleDescription;

	// Plain state changes never reach ag-grid on their own — it renders full-width rows (and any
	// other cell) imperatively, not by reacting to React prop changes. This runs post-commit (via
	// useLayoutEffect, before paint) so contextRef above is already up to date, then forces ag-grid
	// to re-read both the row's height and its rendered content in one pass. refreshCells does not
	// reach full-width rows (ag-grid treats them outside the normal per-column cell pipeline) — a
	// level/description toggle on an already-open row needs an explicit redraw instead. A row that's
	// only just been inserted this pass already mounts fresh off the current context, so skip the
	// redraw there — redrawing it in the same tick as the insert drops the row instead of refreshing it.
	useLayoutEffect(() => {
		const api = gridRef.current?.api;
		if (!api) return;
		const isSameRowAsLastPass = lastExpandedRowIdRef.current === expandedRowId;
		lastExpandedRowIdRef.current = expandedRowId;

		if (expandedRowId) {
			const node = api.getRowNode(superRowId(expandedRowId));
			if (node?.data) {
				const expandedRanks = levelsExpanded[expandedRowId] ?? EMPTY_RANKS;
				const height = estimateHeight(
					node.data.competency_id,
					expandedRanks,
					!!descriptionExpanded[expandedRowId]
				);
				node.setRowHeight(height);
				api.onRowHeightChanged();
				if (isSameRowAsLastPass) {
					api.redrawRows({ rowNodes: [node] });
				}
			}
		}
		api.refreshCells({ force: true });
	}, [expandedRowId, levelsExpanded, descriptionExpanded]);

	// A new page/sort/filter result can't be reliably matched back up with a previously-open super
	// row, so collapse and drop all inner expansion state whenever the underlying data set changes.
	useEffect(() => {
		removeExpandedRow();
		setExpandedRowId(null);
		setLevelsExpanded({});
		setDescriptionExpanded({});
	}, [rows]);

	// Memoized on `rows` alone: the Table wrapper re-applies its `rowData` grid option whenever this
	// array's reference changes, and — because `getRowId` is set — reconciles the grid's rows against
	// exactly what's in it, which would silently drop the applyTransaction-added super row on every
	// unrelated state change (a level toggle, description toggle, etc.) if recomputed every render.
	const rowData: SuperRowRow<TRow>[] = useMemo(
		() => rows.map((row) => ({ ...row, _rowId: getRowId(row) }) as SuperRowRow<TRow>),

		[rows]
	);

	const isFullWidthRow = (params: IsFullWidthRowParams<SuperRowRow<TRow>>): boolean =>
		params.rowNode.data?.superRow === true;

	const getRowHeight = (params: RowHeightParams<SuperRowRow<TRow>>): number | undefined => {
		const row = params.data;
		if (!row?.superRow || !row.parentRowId) return undefined;
		const expandedRanks = levelsExpanded[row.parentRowId] ?? EMPTY_RANKS;
		return estimateHeight(row.competency_id, expandedRanks, !!descriptionExpanded[row.parentRowId]);
	};

	return { gridRef, contextRef, rowData, isFullWidthRow, getRowHeight };
}
